import { AnyAction, ActionCreator, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { toast } from 'react-toastify';
import uuidv4 from 'uuid/v4';
import {
  defaultBackgroundData,
  defaultBackgroundImageData,
  defaultBorderData,
  defaultBoxShadowData,
  defaultSizeLocationData,
  FOOTER_SECTION_ID,
  HEADER_SECTION_ID,
  widgetTypes,
} from '../../../builder/util/constants';
import { RootState } from '../reducer';
import {
  addChild,
  cannotAddToDefaultSections,
  removeDefaultWidgets,
  updatePageContent,
  SET_PENDING_DEFAULT_WIDGET_FETCHES,
  FETCH_PAGE_FAILED,
  fetchPage,
} from './sites';
import { isContentLoaded } from 'shared/util/pageUtils';
import { syncAddAcrossRepeaterContainers } from 'shared/widgets/RepeaterWidget/RepeaterUtils';

export interface Widget {
  id: string;
  type: string;
  tabIndex?: number;
  config: {
    style?: string; // for Navigation widget, e.g., 'Horizontal' or 'Vertical'
    currentMenuItem?: number;
    currentTabIndex?: number;
    currentSlideIndex?: number;
    xLocation: number;
    yLocation: number;
    width: number;
    height: number;
    isHorizonWidget: boolean;
    enableFullScreen: boolean;
    uniqueId: string;
    lineColor: string;
    visible: boolean;
    isCustomWidget: boolean;
    parentId: string;
    spacing: {
      top: string;
      right: string;
      bottom: string;
      left: string;
    };
  };
  children: string[];
  defaultWidget?: boolean;
}

export interface WidgetsState {
  selectedWidgetIds: string[];
}

export const initialState: WidgetsState = {
  selectedWidgetIds: [],
};

const SET_SELECTED_WIDGET_IDS = 'SET_SELECTED_WIDGET_IDS';
const CLEAR_SELECTED_WIDGET_IDS = 'CLEAR_SELECTED_WIDGET_IDS';

export const setSelectedWidgetIds: ActionCreator<AnyAction> = (id: string) => ({
  type: SET_SELECTED_WIDGET_IDS,
  id,
});

export const clearSelectedWidgetIds: ActionCreator<AnyAction> = () => ({
  type: CLEAR_SELECTED_WIDGET_IDS,
});

/**
 * Selects the best existing parent for a group of widgets.
 *
 * @param content - Current page widget content.
 * @param selectedWidgets - Widgets selected for grouping.
 * @param groupingSectionId - Selected header or footer section, when present.
 * @returns The selected parent widget, or undefined when none is suitable.
 */
const selectGroupingParent = (
  content: Record<string, any>,
  selectedWidgets: Widget[],
  groupingSectionId: string | null
): Widget | undefined => {
  if (groupingSectionId) {
    return content[groupingSectionId];
  }

  // This is a fairly naive approach to deciding which widget should be the parent.
  const possibleParents = selectedWidgets.filter(
    (widget: Widget) =>
      (widget.config.visible || widget.config.visible === undefined) &&
      (widget.type === widgetTypes.Section ||
        widget.type === widgetTypes.Card ||
        widget.type === widgetTypes.Form ||
        widget.type === widgetTypes.Banner ||
        widget.type === widgetTypes.Tabs ||
        widget.type === widgetTypes.AccordionWidget ||
        (widget.type === widgetTypes.Navigation &&
          widget.config.style !== 'Horizontal') ||
        widget.type === widgetTypes.Slideshow)
  );

  // Filter possible parents to include only Tabs, AccordionWidget, Slideshow or Navigation and use these for priority
  const priorityParents = possibleParents.filter(
    widget =>
      widget.type === widgetTypes.Tabs ||
      widget.type === widgetTypes.AccordionWidget ||
      widget.type === widgetTypes.Navigation ||
      widget.type === widgetTypes.Slideshow
  );

  // If priority parents are found, find the largest among them
  if (priorityParents.length > 0) {
    return priorityParents.reduce((largest, widget) => {
      const widgetArea = calculateArea(widget);
      const largestArea = calculateArea(largest);
      return widgetArea > largestArea ? widget : largest;
    }, priorityParents[0]);
  }

  // If no priority parents are found, fall back to logic that uses largest of all possible parents
  if (possibleParents.length > 0) {
    return possibleParents.reduce((largest, widget) => {
      const widgetArea = calculateArea(widget);
      const largestArea = calculateArea(largest);
      return widgetArea > largestArea ? widget : largest;
    }, possibleParents[0]);
  }

  return undefined;
};

export const groupWidgets: ActionCreator<ThunkAction<
  void,
  RootState,
  unknown,
  AnyAction
>> = (page: Page, siteId: number) => {
  return (dispatch, getState) => {
    if (
      typeof siteId !== 'number' ||
      siteId < 0 ||
      typeof page.id !== 'number' ||
      page.id < 0
    ) {
      console.error(
        'groupWidgets: invalid siteId or pageId provided:',
        siteId,
        page.id
      );
      return;
    }
    const state = getState();
    if (state.widgets.selectedWidgetIds.length === 0) return;

    const newContent = { ...page.current.content };
    const isHeaderContainerSelected = state.widgets.selectedWidgetIds.includes(
      HEADER_SECTION_ID
    );
    const isFooterContainerSelected = state.widgets.selectedWidgetIds.includes(
      FOOTER_SECTION_ID
    );

    if (isHeaderContainerSelected && isFooterContainerSelected) {
      toast('Header and footer containers cannot be grouped together.', {
        type: 'warning',
        theme: 'colored',
      });
      return;
    }

    const groupingSectionId = isHeaderContainerSelected
      ? HEADER_SECTION_ID
      : isFooterContainerSelected
      ? FOOTER_SECTION_ID
      : null;

    if (groupingSectionId) {
      normalizeDefaultSectionStructure(newContent, groupingSectionId);
    }

    const selectedWidgets = Object.values(newContent).filter(
      (widget: Widget) =>
        state.widgets.selectedWidgetIds.includes(widget.id) &&
        // filtering out 'root' in case it's selected while clicking & dragging
        widget.id !== 'root' &&
        widget.config.visible !== false
    );
    // Check if any selected widget type is in cannotAddToDefaultSections
    const invalidWidgets = selectedWidgets.filter(
      widget =>
        cannotAddToDefaultSections.includes(widget.type) ||
        (widget.type === widgetTypes.Navigation &&
          widget.config.style === 'Vertical')
    );

    if (
      invalidWidgets.length > 0 &&
      (selectedWidgets.includes(newContent[HEADER_SECTION_ID]) ||
        selectedWidgets.includes(newContent[FOOTER_SECTION_ID]))
    ) {
      toast(
        `${invalidWidgets[0].type} widgets cannot be added to the header or footer.`,
        {
          type: 'warning',
          theme: 'colored',
        }
      );
      return;
    }

    let parent = selectGroupingParent(
      newContent,
      selectedWidgets,
      groupingSectionId
    );

    // If the parent already has children, check if the selected widgets are already grouped
    if (parent && parent.children.length > 0) {
      // get all descendants (including nested) plus the parent itself
      const groupSet = new Set([
        parent.id,
        ...getAllChildrenRecursively(page.current, parent.id),
      ]);
      // if every selected widget is in the group, show the toast
      const allSelectedAreGrouped = state.widgets.selectedWidgetIds.every(id =>
        groupSet.has(id)
      );
      if (allSelectedAreGrouped) {
        toast('Selected widgets are already grouped', {
          type: 'warning',
          theme: 'colored',
        });
        return;
      }
    }

    const {
      width: boundingBoxWidth,
      height: boundingBoxHeight,
    } = calculateBoundingBox(selectedWidgets);

    const parentWidth = parent?.config.enableFullScreen
      ? 1600
      : parent?.config.width;

    const defaultSectionId = detectDefaultSectionScope(
      newContent,
      selectedWidgets
    );
    const isDefaultGrouping =
      Boolean(defaultSectionId) ||
      selectedWidgets.some((widget: Widget) => widget.defaultWidget === true);

    if (
      !groupingSectionId &&
      (!parent ||
        parentWidth < boundingBoxWidth ||
        parent.config.height < boundingBoxHeight)
    ) {
      parent = createNewParentWidget(selectedWidgets, isDefaultGrouping);
      newContent[parent.id] = parent;
    }

    if (!parent) return;

    if (
      parent.type === widgetTypes.AccordionWidget &&
      parent.config.currentTabIndex === -1
    ) {
      toast('Cannot add widgets to the accordion if no panel is selected', {
        type: 'warning',
        theme: 'colored',
      });
      return;
    }
    const childIds = state.widgets.selectedWidgetIds.filter(
      id =>
        id !== parent.id &&
        !state.widgets.selectedWidgetIds.includes(
          newContent[id]?.config?.parentId
        ) &&
        (newContent[id]?.config.visible ||
          newContent[id]?.config.visible === undefined)
    );
    // Grab the default values of child widgets before updating relationships
    const childValues = childIds.map(childId => {
      const childWidget = newContent[childId];
      if (
        childWidget.config.visible ||
        childWidget.config.visible === undefined
      ) {
        const previousParentId = childWidget?.config.parentId;
        const previousParent = newContent[previousParentId];
        return {
          childId,
          childWidget,
          wasDefault: previousParent && isDefaultWidget(previousParent),
        };
      } else {
        return {
          childId,
          childWidget: null,
          wasDefault: false,
        };
      }
    });

    // Update parent-child relationships
    updateParentChildRelationships(
      newContent,
      parent,
      childValues.map(v => v.childId)
    );

    const groupedDefaultChildIds = childValues
      .map(v => v.childId)
      .filter(childId => newContent[childId]?.defaultWidget === true);

    if (isDefaultWidget(parent) && groupedDefaultChildIds.length > 0) {
      syncGroupedDefaultWidgetsAcrossPages(
        state,
        siteId,
        page as any,
        newContent,
        parent,
        groupedDefaultChildIds,
        dispatch,
        groupingSectionId
      );
    }

    // Handle propagation of widget changes after relationships are updated
    childValues.forEach(({ childId, childWidget, wasDefault }) => {
      const isNowDefault = isDefaultWidget(parent);

      // If the child was previously grouped with a default widget parent and is no longer default, remove the default
      // status from the child and propagate the changes to other pages
      if (wasDefault && !isNowDefault) {
        propagateWidgetChanges(
          state,
          siteId,
          page.id,
          childId,
          childWidget,
          false,
          dispatch
        );
      }

      // If the child is now grouped with a default widget parent, add the default status to the child and propagate
      // the changes to other pages
      if (!wasDefault && isNowDefault) {
        propagateWidgetChanges(
          state,
          siteId,
          page.id,
          childId,
          childWidget,
          true,
          dispatch
        );
      }
    });

    if (groupingSectionId) {
      normalizeDefaultSectionStructure(newContent, groupingSectionId);
    }

    dispatch(
      updatePageContent(siteId, page.id, {
        siteId,
        pageId: page.id,
        page: {
          ...page,
          current: {
            ...page.current,
            content: newContent,
          },
        },
      })
    );

    // Sync new children to all sibling repeater containers
    if (parent.type === widgetTypes.Section) {
      const repeaterParent = newContent[parent.config.parentId];
      if (repeaterParent?.type === widgetTypes.RepeaterWidget) {
        const updatedPageVersion = {
          ...page.current,
          content: newContent,
        };
        childIds.forEach(childId => {
          const childWidget = newContent[childId];
          syncAddAcrossRepeaterContainers({
            addedWidget: childWidget,
            currentPage: updatedPageVersion,
            siteId,
            pageId: page.id,
            dispatch,
          });
        });
      }
    }

    dispatch({ type: CLEAR_SELECTED_WIDGET_IDS });
  };
};

export const ungroupWidgets: ActionCreator<ThunkAction<
  void,
  RootState,
  unknown,
  AnyAction
>> = (page: Page, siteId: number) => {
  return (dispatch, getState) => {
    const state = getState();
    const { updatedContent, ungroupedWidgets } = ungroupSelectedWidgets(
      page.current.content,
      state.widgets.selectedWidgetIds
    );

    dispatch(
      updatePageContent(siteId, page.id, {
        siteId,
        pageId: page.id,
        page: {
          ...page,
          current: {
            ...page.current,
            content: updatedContent,
          },
        },
      })
    );

    const sitePages = state.sites.data.find(site => site.siteId === siteId)
      ?.pages;
    const defaultScopedWidgetIds = ungroupedWidgets
      .filter(widget => widget.wasDefaultScoped)
      .map(widget => widget.id);

    if (sitePages && defaultScopedWidgetIds.length > 0) {
      const skippedPlaceholderIds: number[] = [];

      Object.keys(sitePages).forEach(otherPageId => {
        const parsedPageId = parseInt(otherPageId, 10);
        if (parsedPageId === page.id) return;

        const otherPage = sitePages[parsedPageId];
        if (!otherPage?.current?.content) return;

        // Guard: skip placeholder pages — ungroupSelectedWidgets spreads
        // templates[0].widgets, which breaks the reference sentinel used by
        // filterValidPages and causes handleSavePage to wipe real server content.
        // FETCH_PAGE_FULFILLED will propagate the updated header/footer (without
        // the ungrouped widget) when each placeholder is eventually fetched.
        if (!isContentLoaded(otherPage.current)) {
          skippedPlaceholderIds.push(parsedPageId);
          return;
        }

        // Collect widget IDs that actually exist on this page and need to be
        // removed entirely. We check for existence (not defaultWidget === true)
        // because propagateWidgetChanges adds widgets via addChild whose
        // reducer else-branch omits the defaultWidget flag — so the widget
        // lands on other pages without defaultWidget: true even though it was
        // propagated from the header. UUID uniqueness guarantees any matching
        // widget was propagated (not independently placed).
        const widgetIdsToRemove = defaultScopedWidgetIds.filter(
          widgetId => otherPage.current.content[widgetId] !== undefined
        );

        const {
          updatedContent: mirroredUpdatedContent,
        } = ungroupSelectedWidgets(
          otherPage.current.content,
          defaultScopedWidgetIds
        );

        // Remove default widgets entirely from other pages — they only existed
        // there because of the header/footer grouping. On the source page the
        // widget stays detached (user can reposition or delete it), but on all
        // other pages it should disappear completely.
        widgetIdsToRemove.forEach(widgetId => {
          delete mirroredUpdatedContent[widgetId];
        });

        dispatch(
          updatePageContent(siteId, parsedPageId, {
            siteId,
            pageId: parsedPageId,
            page: {
              ...otherPage,
              current: {
                ...otherPage.current,
                content: mirroredUpdatedContent,
              },
            },
          })
        );
      });

      // For skipped placeholder pages, set the pending flag and kick off
      // background fetches so FETCH_PAGE_FULFILLED can propagate the updated
      // header/footer state before the next save.
      if (skippedPlaceholderIds.length > 0) {
        dispatch({ type: SET_PENDING_DEFAULT_WIDGET_FETCHES, value: true });
        skippedPlaceholderIds.forEach(pid =>
          (dispatch as any)(fetchPage(siteId, pid)).catch((error: unknown) => {
            dispatch({ type: FETCH_PAGE_FAILED, siteId, pageId: pid, error });
            console.error(
              'fetchPage background fetch failed for page:',
              pid,
              error
            );
          })
        );
      }
    }

    dispatch({ type: CLEAR_SELECTED_WIDGET_IDS });
  };
};

const widgetsReducer = (
  state = initialState,
  action: AnyAction
): WidgetsState => {
  switch (action.type) {
    case SET_SELECTED_WIDGET_IDS:
      return {
        ...state,
        selectedWidgetIds: state.selectedWidgetIds.includes(action.id)
          ? state.selectedWidgetIds.filter(
              selectedId => selectedId !== action.id
            )
          : [...state.selectedWidgetIds, action.id],
      };
    case CLEAR_SELECTED_WIDGET_IDS:
      return {
        ...state,
        selectedWidgetIds: [],
      };
    default:
      return state;
  }
};

// Selectors
export const getSelectedWidgetIds = (state: WidgetsState): string[] => {
  return state.selectedWidgetIds;
};

// Utility functions
export const createNewParentWidget = (
  selectedWidgets: Widget[],
  defaultWidget = false
): Widget => {
  const widgetId = uuidv4();
  const { minX, minY, width, height } = calculateBoundingBox(selectedWidgets);

  const parentId =
    selectedWidgets.find(widget => widget.config.parentId)?.config.parentId ||
    undefined;

  const newWidgetConfig = {
    ...defaultBackgroundData,
    ...defaultBackgroundImageData,
    ...defaultSizeLocationData,
    ...defaultBorderData,
    ...defaultBoxShadowData,
    spacing: {
      top: '',
      right: '',
      bottom: '',
      left: '',
    },
    parentId: parentId,
    isHorizonWidget: true,
    enableFullScreen: false,
    uniqueId: 'section345',
    lineColor: 'black',
    xLocation: minX - 20,
    yLocation: minY - 20,
    height: height + 40,
    width: width + 40,
    visible: true,
    isCustomWidget: false,
  };

  return {
    id: widgetId,
    type: widgetTypes.Section,
    config: newWidgetConfig,
    children: [],
    tabIndex: -1,
    defaultWidget,
  };
};

// These are only widgets that have panels/tabs/slides/link sections that can be visible or not
export const isNestedWidgetType = (widget: Widget) =>
  widget?.type === widgetTypes.Tabs ||
  widget?.type === widgetTypes.AccordionWidget ||
  widget?.type === widgetTypes.Slideshow ||
  (widget?.type === widgetTypes.Navigation &&
    widget?.config.style === 'Vertical');

// Function to determine if the existing parent widget should be used instead of the new parent widget
export const shouldUseExistingParent = (
  existingParent: Widget, // The existing parent widget
  newParent: Widget // The new parent widget
): boolean => {
  // Return true if both the existing parent and new parent are of nested widget types
  return isNestedWidgetType(existingParent) && isNestedWidgetType(newParent);
};

// Utility function to determine if a widget is a default widget
const isDefaultWidget = (parentWidget: Widget | undefined): boolean => {
  return (
    parentWidget?.defaultWidget === true ||
    parentWidget?.id === HEADER_SECTION_ID ||
    parentWidget?.id === FOOTER_SECTION_ID
  );
};

const addChildrenToParent = (
  newContent: any,
  parent: Widget,
  parentId: string,
  childIds: string[]
) => {
  // Create a set of existing children for the parent widget
  const existingChildren = new Set(newContent[parentId].children);
  // Iterate over each child ID
  childIds.forEach(childId => {
    // Remove childId from any previous parent's children array
    let prevParentId = newContent[childId]?.config?.parentId;
    if (!prevParentId) {
      // If no parentId is set, check if the child is in the parent's children array
      for (const possibleParent of Object.values(newContent)) {
        if ((possibleParent as Widget)?.children?.includes(childId)) {
          prevParentId = (possibleParent as Widget).id;
          break;
        }
      }
    }
    if (
      prevParentId &&
      prevParentId !== 'root' &&
      prevParentId !== parent.id &&
      newContent[prevParentId]
    ) {
      newContent[prevParentId].children = newContent[
        prevParentId
      ].children.filter((id: string) => id !== childId);
    }
    // Get the existing parent of the child widget
    const existingParent = newContent[prevParentId];
    // Check if the existing parent should be used
    if (!shouldUseExistingParent(existingParent, parent)) {
      // Add the child ID to the set of existing children
      existingChildren.add(childId);
      // Update the parent ID of the child widget to the new parent ID
      newContent[childId].config.parentId = parent.id;
      // Update the 'defaultWidget' property based on the parent
      newContent[childId].defaultWidget = isDefaultWidget(parent);
      // If the parent widget is a nested widget type, update the tab index of the child widget
      if (isNestedWidgetType(parent)) {
        newContent[childId].tabIndex = getParentConfigIndex(parent) ?? 0;
      }
    } else {
      // If the existing parent should be used, update the parent ID of the child widget to the existing parent ID
      newContent[childId].config.parentId = existingParent.id;
    }
  });
  // Update the children of the parent widget with the new set of existing children
  newContent[parentId].children = Array.from(existingChildren);
};

export const updateParentChildRelationships = (
  newContent: any,
  parent: Widget,
  childIds: string[]
) => {
  // Add the child widgets to the parent widget
  addChildrenToParent(newContent, parent, parent.id, childIds);
  let currentParent = parent; // Start with the initial parent widget
  if (currentParent?.config) {
    const visitedParents = new Set<string>(); // Set to track visited parent widgets
    // Traverse up the parent hierarchy
    while (currentParent?.config.parentId) {
      // Break the loop if a circular reference is detected
      if (visitedParents.has(currentParent.id)) {
        break;
      }
      // Add the current parent ID to the visited set
      visitedParents.add(currentParent.id);
      // Move to the next parent in the hierarchy
      currentParent = newContent[currentParent.config.parentId];
      // If the next parent exists, add the child widgets to it
      if (currentParent) {
        addChildrenToParent(newContent, parent, currentParent.id, childIds);
      }
    }
  }
};

type UngroupedWidgetMeta = {
  id: string;
  wasDefaultScoped: boolean;
};

const ungroupSelectedWidgets = (
  content: any,
  selectedWidgetIds: string[]
): {
  updatedContent: any;
  ungroupedWidgets: UngroupedWidgetMeta[];
} => {
  const updatedContent = { ...content };
  const selectedIds = selectedWidgetIds.filter(
    id => id !== HEADER_SECTION_ID && id !== FOOTER_SECTION_ID
  );
  const widgetsToUngroup = Object.values(
    updatedContent
  ).filter((widget: Widget) => selectedIds.includes(widget.id)) as Widget[];
  const ungroupedWidgets: UngroupedWidgetMeta[] = [];

  widgetsToUngroup.forEach((widget: Widget) => {
    // Determine parentId:
    let parentId = widget.config.parentId;
    if (!parentId) {
      // If no config.parentId, check if any other selected widget has this widget as a child
      for (const possibleParent of widgetsToUngroup) {
        if (
          possibleParent.id !== widget.id &&
          Array.isArray(possibleParent.children) &&
          possibleParent.children.includes(widget.id)
        ) {
          parentId = possibleParent.id;
        }
      }
    }
    if (parentId && updatedContent[parentId]) {
      const parent = updatedContent[parentId];
      const wasDefaultScoped =
        isDefaultWidget(parent) ||
        findDefaultSectionAncestor(updatedContent, widget.id) !== null;
      parent.children = parent.children.filter(
        (childId: string) => childId !== widget.id
      );
      widget.config.parentId = undefined; // Remove parentId from the widget
      widget.tabIndex = -1; // Reset tabIndex for the ungrouped widget
      widget.defaultWidget = false;
      ungroupedWidgets.push({
        id: widget.id,
        wasDefaultScoped,
      });
    }
  });

  return { updatedContent, ungroupedWidgets };
};

// Function to calculate the bounding box of a set of widgets. Use this to build a new parent widget if one doesn't exist.
export const calculateBoundingBox = (widgets: Widget[]) => {
  const xLocations = widgets.map(widget => widget.config.xLocation);
  const yLocations = widgets.map(widget => widget.config.yLocation);
  const widths = widgets.map(widget => widget.config.width);
  const heights = widgets.map(widget => widget.config.height);

  const minX = Math.min(...xLocations);
  const minY = Math.min(...yLocations);
  const maxX = Math.max(...xLocations.map((x, i) => x + widths[i]));
  const maxY = Math.max(...yLocations.map((y, i) => y + heights[i]));

  return {
    minX,
    minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

// Function to get the index of the parent widget's configuration based on its type (ideally these should all
// use the same config property [currentTabIndex], but leaving it for now to avoid breaking changes)
export function getParentConfigIndex(parent: Widget): number | undefined {
  switch (parent.type) {
    case widgetTypes.Slideshow:
      return parent.config.currentSlideIndex;
    case widgetTypes.Tabs:
    case widgetTypes.AccordionWidget:
      return parent.config.currentTabIndex;
    case widgetTypes.Navigation:
      return parent.config.currentMenuItem;
    default:
      return parent.tabIndex; // Default case for other widget types
  }
}

/* this is a utility function that assists with changes to default widgets; since these widgets are meant to be on each page 
of a site, grouping and ungrouping children with default parents will need to propagate across all of a site's pages */
const propagateWidgetChanges = (
  state: RootState,
  siteId: number,
  pageId: number,
  widgetId: string,
  newWidget: Widget,
  isDefault: boolean,
  dispatch: Dispatch
) => {
  const sitePages = state.sites.data.find(site => site.siteId === siteId).pages;

  Object.keys(sitePages).forEach(otherPageId => {
    if (parseInt(otherPageId, 10) !== pageId) {
      if (isDefault && sitePages[otherPageId]) {
        // Add the widget to other pages if widget previously wasn't default.
        const parentId = newWidget.config.parentId;
        dispatch(
          addChild(
            siteId,
            parseInt(otherPageId, 10),
            parentId,
            newWidget.type,
            newWidget.config,
            0,
            widgetId,
            newWidget.tabIndex
          )
        );
      } else {
        // Remove the widget from other pages if widget previously was default
        dispatch(
          removeDefaultWidgets(siteId, parseInt(otherPageId, 10), widgetId)
        );
      }
    }
  });
};

const detectDefaultSectionScope = (
  content: Record<string, any>,
  selectedWidgets: Widget[]
): string | null => {
  for (const widget of selectedWidgets) {
    const defaultSectionId = findDefaultSectionAncestor(content, widget.id);
    if (defaultSectionId) {
      return defaultSectionId;
    }
  }
  return null;
};

const findDefaultSectionAncestor = (
  content: Record<string, any>,
  widgetId: string
): string | null => {
  let currentId = widgetId;
  const visited = new Set<string>();

  while (currentId && currentId !== 'root' && !visited.has(currentId)) {
    if (currentId === HEADER_SECTION_ID || currentId === FOOTER_SECTION_ID) {
      return currentId;
    }
    visited.add(currentId);
    currentId = content[currentId]?.config?.parentId;
  }

  return null;
};

const ensureChildInParent = (
  content: Record<string, any>,
  parentId: string,
  childId: string
) => {
  if (!content[parentId]) return;
  const parentChildren = Array.isArray(content[parentId].children)
    ? [...content[parentId].children]
    : [];
  if (!parentChildren.includes(childId)) {
    parentChildren.push(childId);
  }
  content[parentId] = {
    ...content[parentId],
    children: parentChildren,
  };
};

const removeChildFromParent = (
  content: Record<string, any>,
  parentId: string,
  childId: string
) => {
  if (!content[parentId] || !Array.isArray(content[parentId].children)) return;
  content[parentId] = {
    ...content[parentId],
    children: content[parentId].children.filter((id: string) => id !== childId),
  };
};

const syncGroupedDefaultWidgetsAcrossPages = (
  state: RootState,
  siteId: number,
  page: Page,
  sourceContent: Record<string, any>,
  parent: Widget,
  childIds: string[],
  dispatch: Dispatch,
  groupingSectionId: string | null = null
) => {
  const sitePages = state.sites.data.find(site => site.siteId === siteId)
    ?.pages;
  if (!sitePages) return;

  const sourceSectionId = findDefaultSectionAncestor(sourceContent, parent.id);

  const skippedPlaceholderIds: number[] = [];

  Object.keys(sitePages).forEach(otherPageId => {
    const parsedPageId = parseInt(otherPageId, 10);
    if (parsedPageId === page.id) return;

    const otherPage = sitePages[parsedPageId];
    if (!otherPage?.current?.content) return;

    // Guard: skip placeholder pages — spreading templates[0].widgets would
    // create a new object, breaking the reference sentinel used by
    // filterValidPages.  That causes handleSavePage to overwrite real server
    // content with template-level content (content wipe).
    // FETCH_PAGE_FULFILLED propagates defaultWidget changes from the reference
    // page when each placeholder is eventually fetched.
    if (!isContentLoaded(otherPage.current)) {
      skippedPlaceholderIds.push(parsedPageId);
      return;
    }

    const targetContent: Record<string, any> = {
      ...otherPage.current.content,
    };
    if (groupingSectionId && parent.id === groupingSectionId) {
      normalizeDefaultSectionStructure(targetContent, groupingSectionId);
    }

    const sourceParent = sourceContent[parent.id];
    if (!sourceParent) return;

    const preferredParentId = sourceParent.config?.parentId;
    const resolvedParentId =
      (preferredParentId && targetContent[preferredParentId]
        ? preferredParentId
        : null) ||
      sourceSectionId ||
      preferredParentId;

    if (!resolvedParentId || !targetContent[resolvedParentId]) return;

    const sourceSection =
      sourceSectionId && sourceContent[sourceSectionId]
        ? sourceContent[sourceSectionId]
        : null;
    const targetSection =
      sourceSectionId && targetContent[sourceSectionId]
        ? targetContent[sourceSectionId]
        : null;

    const sourceParentConfig = sourceParent.config || {};
    const nextParentConfig = {
      ...sourceParentConfig,
      parentId: resolvedParentId,
    };

    if (sourceSection && targetSection) {
      const relParentX =
        (sourceParentConfig.xLocation || 0) -
        (sourceSection.config?.xLocation || 0);
      const relParentY =
        (sourceParentConfig.yLocation || 0) -
        (sourceSection.config?.yLocation || 0);
      nextParentConfig.xLocation =
        (targetSection.config?.xLocation || 0) + relParentX;
      nextParentConfig.yLocation =
        (targetSection.config?.yLocation || 0) + relParentY;
    }

    const existingParent = targetContent[parent.id];
    const existingParentChildren = Array.isArray(existingParent?.children)
      ? existingParent.children
      : Array.isArray(sourceParent.children)
      ? sourceParent.children
      : [];
    targetContent[parent.id] = {
      ...(existingParent || sourceParent),
      config: {
        ...(existingParent?.config || {}),
        ...nextParentConfig,
      },
      defaultWidget: true,
      children: Array.from(new Set(existingParentChildren)).filter(
        (id: string) => id !== parent.id && Boolean(targetContent[id])
      ),
    };

    ensureChildInParent(targetContent, resolvedParentId, parent.id);

    childIds.forEach(childId => {
      const sourceChild = sourceContent[childId];
      const targetChild = targetContent[childId];
      if (!sourceChild || !targetChild) return;

      const previousParentId = targetChild.config?.parentId;
      if (previousParentId && previousParentId !== parent.id) {
        removeChildFromParent(targetContent, previousParentId, childId);
      }

      const nextChildConfig = {
        ...(targetChild.config || {}),
        parentId: parent.id,
      };

      if (sourceSection && targetSection) {
        const relChildX =
          (sourceChild.config?.xLocation || 0) -
          (sourceSection.config?.xLocation || 0);
        const relChildY =
          (sourceChild.config?.yLocation || 0) -
          (sourceSection.config?.yLocation || 0);
        nextChildConfig.xLocation =
          (targetSection.config?.xLocation || 0) + relChildX;
        nextChildConfig.yLocation =
          (targetSection.config?.yLocation || 0) + relChildY;
      }

      targetContent[childId] = {
        ...targetChild,
        config: nextChildConfig,
        defaultWidget: true,
      };
    });

    const sourceParentChildren = Array.isArray(sourceParent.children)
      ? sourceParent.children
      : [];
    const targetParentChildren = Array.isArray(
      targetContent[parent.id].children
    )
      ? targetContent[parent.id].children
      : [];
    const orderedChildren = [
      ...sourceParentChildren,
      ...targetParentChildren,
    ].filter((id: string) => id !== parent.id);
    const finalParentChildren = Array.from(new Set(orderedChildren)).filter(
      (id: string) =>
        Boolean(targetContent[id]) &&
        targetContent[id]?.config?.parentId === parent.id
    );
    targetContent[parent.id] = {
      ...targetContent[parent.id],
      children: finalParentChildren,
    };

    if (groupingSectionId && parent.id === groupingSectionId) {
      normalizeDefaultSectionStructure(targetContent, groupingSectionId);
    }

    dispatch(
      updatePageContent(siteId, parsedPageId, {
        siteId,
        pageId: parsedPageId,
        page: {
          ...otherPage,
          current: {
            ...otherPage.current,
            content: targetContent,
          },
        },
      })
    );
  });

  // For placeholder pages that were skipped above, set the pending-fetch flag
  // and kick off background fetches.  When each page loads, FETCH_PAGE_FULFILLED
  // will propagate the current in-memory header/footer (including the newly
  // grouped widget) from the reference page, so those pages receive the change
  // before the next save.
  if (skippedPlaceholderIds.length > 0) {
    dispatch({ type: SET_PENDING_DEFAULT_WIDGET_FETCHES, value: true });
    skippedPlaceholderIds.forEach(pid =>
      (dispatch as any)(fetchPage(siteId, pid)).catch((error: unknown) => {
        dispatch({ type: FETCH_PAGE_FAILED, siteId, pageId: pid, error });
        console.error(
          'fetchPage background fetch failed for page:',
          pid,
          error
        );
      })
    );
  }
};

const normalizeDefaultSectionStructure = (
  content: Record<string, any>,
  sectionId: string
) => {
  const sectionWidget = content[sectionId];
  if (!sectionWidget?.config) return;

  content[sectionId] = {
    ...sectionWidget,
    config: {
      ...sectionWidget.config,
      parentId: 'root',
    },
  };

  Object.keys(content).forEach(widgetId => {
    if (widgetId === 'root' || widgetId === sectionId) return;
    const widget = content[widgetId];
    if (!widget || !Array.isArray(widget.children)) return;
    if (!widget.children.includes(sectionId)) return;
    content[widgetId] = {
      ...widget,
      children: widget.children.filter((id: string) => id !== sectionId),
    };
  });

  ensureChildInParent(content, 'root', sectionId);

  const existingSectionChildren = Array.isArray(content[sectionId]?.children)
    ? content[sectionId].children
    : [];
  const directSectionChildren = Object.keys(content).filter(widgetId => {
    if (widgetId === 'root' || widgetId === sectionId) return false;
    return content[widgetId]?.config?.parentId === sectionId;
  });

  const validExistingSectionChildren = existingSectionChildren.filter(
    (childId: string) =>
      Boolean(content[childId]) &&
      content[childId]?.config?.parentId === sectionId &&
      childId !== sectionId
  );

  content[sectionId] = {
    ...content[sectionId],
    children: Array.from(
      new Set([...validExistingSectionChildren, ...directSectionChildren])
    ),
  };
};

const calculateArea = (widget: Widget): number => {
  const width = widget.config.enableFullScreen ? 1600 : widget.config.width;
  const height = widget.config.height;
  return width * height;
};

// returns an array of the IDs all ancestors of a widget, including children of nested children
export const getAllChildrenRecursively = (
  currentPage: PageVersion,
  widgetId: string
) => {
  let children = [];
  const widget = currentPage.content[widgetId];
  if (widget?.children) {
    children = widget.children;
    widget.children.forEach(childId => {
      children = [
        ...children,
        ...getAllChildrenRecursively(currentPage, childId),
      ];
    });
  }
  return children;
};

// returns an array of the IDs of all visible children (and any ancestors) of a widget, including children of nested
// children; this is useful for determining which widgets should be grouped with a Tabs widget, for example
export const getAllVisibleChildrenRecursively = (
  currentPage: PageVersion,
  widgetId: string
): string[] => {
  let children: string[] = [];
  const widget = currentPage.content[widgetId];
  if (widget?.children) {
    children = widget.children.filter(
      childId =>
        currentPage.content[childId]?.config.visible !== false ||
        (isNestedWidgetType(widget) &&
          getParentConfigIndex(widget) ===
            currentPage.content[childId]?.tabIndex)
    );
    widget.children.forEach(childId => {
      children = [
        ...children,
        ...getAllVisibleChildrenRecursively(currentPage, childId),
      ];
    });
  }
  return children;
};

// returns the number of ancestors that a child widget has; this helps calculate the z-index for deeply nested widgets
// this is a bit of a hack, but it works for now
export const getAncestorCount = (
  currentPage: PageVersion,
  widgetId: string
) => {
  let count = 0;
  let parentId = currentPage.content[widgetId]?.config.parentId;
  while (parentId !== undefined && parentId !== 'root') {
    count += 5;
    parentId = currentPage.content[parentId]?.config.parentId;
  }
  return count;
};

export default widgetsReducer;
